import classNames from "classnames";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { useModalControl } from "../../../../customHooks/useModalControl";
import { formatToCurrency } from "../../../../helperFunctions";
import {
  useApproveLoanRequestMutation,
  useFetchPendingLoanRequestApprovalsQuery,
} from "../../../../redux/features/approvals/approvals.api";
import { LoanRequestApprovalQuery } from "../../../../redux/features/approvals/approvals.type";
import { SingleCooperative } from "../../../../redux/features/cooperative/cooperative.type";
import { SingleLoan } from "../../../../redux/features/loans/loans.type";
import { ApprovalActionDetails } from "../../../../utils/types";
import { ApprovalButtonSet } from "../../../buttons/approvalButtonSet";
import { ConfirmationModal } from "../../../modals/app_modals/confirmationModal";
import { LoanDetailsModal } from "../../../modals/app_modals/loans/loanDetailsModal";
import { SingleLoanRequestApproval } from "./singleLoanRequestApproval";

export const LoansApprovalTable = ({
  singleCooperative,
  refresh,
  pageScrollProgress,
}: {
  singleCooperative: SingleCooperative;
  refresh: () => void;
  pageScrollProgress: number;
}) => {
  const [singleQuery, setSingleQuery] = useState<LoanRequestApprovalQuery>({
    limit: 10,
    offset: 0,
    coop: singleCooperative.id,
  });
  const [selectedEntry, setSelectedEntry] = useState<SingleLoan>();
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [actionDetails, setActionDetails] = useState<ApprovalActionDetails>();

  const modalControls = useModalControl();

  const { modal, modalOpen, handleModalOpen, handleModalClose } = modalControls;

  const {
    data: singleApprovals,
    isFetching: fetchingSingleApprovals,
    refetch: refetchSingleApprovals,
  } = useFetchPendingLoanRequestApprovalsQuery(
    { ...singleQuery },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const [doApproval, { isLoading: approving, isSuccess: approvalSuccess }] =
    useApproveLoanRequestMutation();

  useEffect(() => {
    if (approvalSuccess) {
      refetchSingleApprovals();
      refresh();
      setActionDetails(undefined);
      setSelectedIds([]);
      setSelectedEntry(undefined);
    }
    // eslint-disable-next-line
  }, [approvalSuccess]);

  return (
    <>
      <div className="flex flex-col md:gap-8">
        {selectedIds.length > 0 ? (
          <motion.div
            initial={{ height: 0 }}
            animate={{ height: "auto" }}
            exit={{ height: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className={classNames(
              "max-md:app-body-px md:py-0 z-10 py-4",
              "flex items-center gap-4 justify-between flex-row overflow-hidden",
              {
                "max-md:gap-0": selectedIds.length > 0,
                "max-md:sticky max-md:top-0 max-md:bg-light-green-bg/50 backdrop-blur-lg max-md:border-border-grey-3":
                  pageScrollProgress >= 0 && selectedIds.length > 0,
                "bg-transparent border-none": pageScrollProgress < 0,
              }
            )}
          >
            <ApprovalButtonSet
              onReject={async () => {
                setActionDetails({
                  type: "multi",
                  isApprove: false,
                });

                handleModalOpen("confirmationModal");
              }}
              onAccept={async () => {
                setActionDetails({
                  type: "multi",
                  isApprove: true,
                });

                handleModalOpen("confirmationModal");
              }}
              onClear={() => {
                setSelectedIds([]);
              }}
              acceptText={`Approve (${selectedIds.length})`}
              rejectText={`Decline (${selectedIds.length})`}
              className={classNames(
                "ml-auto overflow-hidden max-md:w-full w-fit"
              )}
              disableActions={approving}
            />
          </motion.div>
        ) : undefined}

        <div
          className={classNames("max-md:app-body-px", {
            "mt-4": selectedIds.length === 0,
          })}
        >
          <SingleLoanRequestApproval
            modalControls={modalControls}
            setSelectedIds={setSelectedIds}
            selectedIds={selectedIds}
            approvals={singleApprovals}
            setActionDetails={setActionDetails}
            setSelectedEntry={setSelectedEntry}
            fetching={fetchingSingleApprovals}
            query={singleQuery}
            setQuery={setSingleQuery}
          />
        </div>
      </div>

      {!!actionDetails ? (
        <ConfirmationModal
          isModalOpen={modalOpen && modal === "confirmationModal"}
          onModalClose={() => {
            handleModalClose();
          }}
          title={`Confirm ${actionDetails?.isApprove ? "Approval" : "Decline"}`}
          subtitle={
            <>
              {actionDetails.type === "single" ? (
                <span>
                  You're about to{" "}
                  {actionDetails.isApprove ? "approve" : "decline"}{" "}
                  <span className="text-pry-green-200 font-medium">
                    {selectedEntry?.MemberID.User.FirstName}{" "}
                    {selectedEntry?.MemberID.User.LastName}'s
                  </span>{" "}
                  loan request of{" "}
                  <span className="text-pry-green-200 font-medium">
                    {formatToCurrency(selectedEntry?.Amount ?? "", {
                      decimalPlaces: 2,
                      currency: "NGN",
                    })}
                  </span>
                  .
                </span>
              ) : undefined}
              {actionDetails.type === "multi" ? (
                <span>
                  You're about to{" "}
                  {actionDetails.isApprove ? "approve" : "decline"} a total of{" "}
                  <span className="text-pry-green-200 font-medium">
                    {selectedIds.length} selected
                  </span>{" "}
                  loan requests.
                </span>
              ) : undefined}
              <br />
              <br />
              ⚠️ This action is <strong>permanent</strong> and cannot be undone.
            </>
          }
          loading={approving}
          proceedButtonText={
            actionDetails?.isApprove ? "Yes, Approve" : "Yes, Decline"
          }
          onProceed={async () => {
            if (actionDetails?.type === "multi") {
              await doApproval({
                coop: singleCooperative.id,
                logs: selectedIds.map((id) => {
                  return {
                    id,
                    answer: actionDetails.isApprove,
                  };
                }),
              });
            } else if (actionDetails?.type === "single") {
              await doApproval({
                coop: singleCooperative.id,
                logs: [
                  {
                    id: actionDetails.singleId!,
                    answer: actionDetails.isApprove,
                  },
                ],
              });
            }
          }}
        />
      ) : undefined}

      {!!selectedEntry ? (
        <LoanDetailsModal
          loanId={selectedEntry.id}
          cooperativeId={singleCooperative.id}
          modalControls={modalControls}
          onClose={() => {
            handleModalClose();
            setSelectedEntry(undefined);
          }}
        />
      ) : undefined}
    </>
  );
};
